import { useCallback, useEffect, useMemo, useState } from 'react';
import { can, faNum, formatPercent, formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { postPrice } from '../api/priceWrite';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import {
  Button,
  Card,
  Chip,
  ErrorRetry,
  Field,
  Kpi,
  Input,
  Modal,
  NumberInput,
  ProvisionalBookBanner,
  Tabs,
  Textarea,
  useConfirm,
  useToast,
} from '../ui';
import { HistoryModal, MarketCards } from './MarketCards';
import { PriceDriftCard } from './PriceDriftCard';
import { TailReconciliationCard } from './TailReconciliationCard';
import { PriceProofCard } from './PriceProofCard';

interface GroupInsightRow {
  key: string;
  materials: number;
  bomLines: number;
  costShare: number;
  orphan: boolean;
  tier: 'mandatory' | 'recommended' | 'optional';
  priced: boolean;
}

interface GroupUnit {
  key: string;
  group: string;
  unit: string;
  main: boolean;
}
interface GroupPrice {
  key: string;
  price: number;
  date: string;
  updated_by: string;
}
interface MaterialRow {
  code: string;
  name: string;
  group: string;
  unit: string;
  consumption: number;
  affectedProducts: number;
  effectivePrice: number | null;
  source: 'actual' | 'group' | null;
  individualPrice: number | null;
}
interface Coverage {
  actualShare: number;
  estimatedShare: number;
  productsWithMissing: number;
  unpricedGroupUnits: number;
  materialsWithIndividualPrice: number;
}
interface ImportPreviewRow {
  line: number;
  code: string;
  price: number | string;
  action: string;
  errors: string[];
}

interface PriceDuty {
  windowDays: number;
  coveragePct: number;
  oldestPriceAgeDays: number | null;
  materialsPriced: number;
  staleMaterials: number;
  staleDays: number;
  updatesThisWeek: number;
  updaters: { actor: string; count: number }[];
  lastUpdate: { actor: string; ts: string } | null;
}

/** Weekly price-duty metric (M7): is someone keeping raw-material prices fresh, and what coverage? */
function PriceDutyCard() {
  const { data: d, status } = useApiResource<PriceDuty>('/api/price-duty');
  if (status === 'loading' || !d) return null;
  const faDate = (ts: string) => {
    try {
      return new Date(ts).toLocaleDateString('fa-IR');
    } catch {
      return ts.slice(0, 10);
    }
  };
  const doneThisWeek = d.updatesThisWeek > 0;
  return (
    <Card title="وظیفهٔ هفتگی قیمت مواد">
      <div className="kpi-grid">
        <Kpi
          label="پوشش بهای واقعی"
          value={formatPercent(d.coveragePct)}
          tone={d.coveragePct >= 0.5 ? 'good' : 'default'}
        />
        <Kpi
          label={`به‌روزرسانی این هفته (${d.windowDays} روز)`}
          value={d.updatesThisWeek}
          tone={doneThisWeek ? 'good' : 'bad'}
        />
        <Kpi
          label="مواد کهنه (نیازمند به‌روزرسانی)"
          value={d.staleMaterials}
          tone={d.staleMaterials > 0 ? 'bad' : 'good'}
          title={`قیمت قدیمی‌تر از ${d.staleDays} روز`}
        />
        <Kpi
          label="کهنگی قدیمی‌ترین قیمت"
          value={d.oldestPriceAgeDays == null ? '—' : `${d.oldestPriceAgeDays} روز`}
        />
      </div>
      <p className="muted" style={{ marginBottom: 0 }}>
        {d.lastUpdate
          ? `آخرین به‌روزرسانی: ${d.lastUpdate.actor} — ${faDate(d.lastUpdate.ts)}. `
          : 'هنوز قیمتی به‌صورت دستی ثبت نشده. '}
        {d.updaters.length
          ? 'مسئولان این هفته: ' + d.updaters.map((u) => `${u.actor} (${u.count})`).join('، ')
          : 'این هفته کسی قیمت‌ها را به‌روز نکرده — وظیفهٔ هفتگی انجام نشده است.'}
      </p>
    </Card>
  );
}

export function PricesScreen({
  focusTab,
  onTabFocused,
}: {
  /** a deep-link (onboarding CTA) forcing a specific tab open; consumed once applied */
  focusTab?: string | null;
  onTabFocused?: () => void;
} = {}) {
  useEffect(() => {
    if (focusTab) onTabFocused?.();
  }, [focusTab, onTabFocused]);
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canEdit = user ? can(user.role, 'price.write') : false;
  const gusRes = useApiResource<GroupUnit[]>('/api/groupunits');
  const pricesRes = useApiResource<{ group: GroupPrice[] }>('/api/prices');
  const insightRes = useApiResource<Record<string, GroupInsightRow>>('/api/prices/group-insight');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // UX-3: the provisional-book signal — the SAME health the dashboard's costBasis reads
  // (params.health on GET /api/assumptions/month, a 'read'-gated route every role can call). A lightweight
  // read; when the month is pool_unset et al. the material-only prices below are provisional. Fail-silent.
  const [bookProvisional, setBookProvisional] = useState(false);
  useEffect(() => {
    let alive = true;
    void api<{ params: { health?: { healthy: boolean } | undefined } }>(
      '/api/assumptions/month',
    ).then((r) => {
      if (!alive || !r.ok) return;
      const h = (r.body as { params: { health?: { healthy: boolean } | undefined } }).params.health;
      setBookProvisional(h ? !h.healthy : false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const insight = insightRes.data ?? {};
  // Pareto ordering: the «اصلی» groups first, then by real cost share — so the ~5 groups that carry most
  // of the material cost sit at the top and the weekly price duty starts where it matters.
  const gus = useMemo(
    () =>
      [...(gusRes.data ?? [])].sort(
        (a, b) =>
          (b.main ? 1 : 0) - (a.main ? 1 : 0) ||
          (insight[b.key]?.costShare ?? 0) - (insight[a.key]?.costShare ?? 0),
      ),
    [gusRes.data, insight],
  );
  const prices = useMemo(() => {
    const map: Record<string, GroupPrice> = {};
    for (const x of pricesRes.data?.group ?? []) map[x.key] = x;
    return map;
  }, [pricesRes.data]);

  const reloadPrices = useCallback(() => {
    gusRes.reload();
    pricesRes.reload();
  }, [gusRes, pricesRes]);

  const loadStatus =
    gusRes.status === 'error' || pricesRes.status === 'error'
      ? 'error'
      : gusRes.status === 'loading' || pricesRes.status === 'loading'
        ? 'loading'
        : 'ready';
  const loadError = gusRes.error ?? pricesRes.error;

  async function save() {
    setBusy(true);
    let n = 0;
    for (const [key, val] of Object.entries(draft)) {
      const price = Number(val);
      if (price > 0) {
        const r = await postPrice('/api/prices/group', { key, price }, confirm);
        if (r.ok) n += 1;
      }
    }
    setBusy(false);
    setDraft({});
    toast(`${n} قیمت ذخیره شد`);
    reloadPrices();
  }

  const [groupChart, setGroupChart] = useState<{ key: string; name: string } | null>(null);
  const [composition, setComposition] = useState<{ key: string; name: string } | null>(null);
  const [simFor, setSimFor] = useState<{ key: string; name: string; price: number } | null>(null);

  const ageDays = (d?: string) =>
    d ? Math.round((Date.now() - new Date(d).getTime()) / 864e5) : null;

  return (
    <>
      {/* UX-3: one calm read-only provisional-book notice, keyed on the dashboard's own health signal. */}
      <ProvisionalBookBanner show={bookProvisional} />
      <Tabs
        storageKey="prices"
        forceId={focusTab}
        items={[
          { id: 'duty', label: 'وظیفهٔ هفتگی', render: () => <PriceDutyCard /> },
          {
            id: 'proof',
            label: 'پشتوانهٔ قیمت',
            render: () => <PriceProofCard canEdit={canEdit} onSaved={reloadPrices} />,
          },
          {
            id: 'drift',
            label: 'انحراف قیمت',
            render: () => <PriceDriftCard canEdit={canEdit} onAdopt={reloadPrices} />,
          },
          {
            id: 'groups',
            label: 'گروه‌ها',
            render: () => (
              <Card title="قیمت گروه‌ها (﷼ به ازای هر واحد)">
                <p className="muted" style={{ marginTop: 0 }}>
                  با ذخیره، بهای همهٔ موتورها دوباره محاسبه می‌شود.
                  {canEdit ? '' : ' (فقط مدیر/خرید می‌تواند تغییر دهد)'}
                </p>
                <p
                  className="muted"
                  style={{
                    marginTop: 4,
                    background: 'var(--surface-2, #f5f5f7)',
                    padding: '8px 10px',
                    borderRadius: 8,
                  }}
                >
                  💡 قیمت‌ها را «بدون حمل» و «بدون ارزش افزوده» وارد کنید. حملِ ورودی از «تنظیمات ←
                  نرخ حمل ورودی» به بهای تمام‌شده افزوده می‌شود؛ مالیات بر ارزش افزوده فقط روی
                  فاکتور/پیش‌فاکتور اعمال می‌شود، نه اینجا.
                </p>
                {(() => {
                  // R-A: nudge the owner to price the high-leverage inputs first — the major (mandatory)
                  // groups, then the repeated (recommended) ones. Orphans/optional are silent.
                  const ins = Object.values(insight);
                  const mand = ins.filter((i) => !i.priced && i.tier === 'mandatory').length;
                  const rec = ins.filter((i) => !i.priced && i.tier === 'recommended').length;
                  if (mand + rec === 0) return null;
                  return (
                    <p
                      className="muted"
                      style={{
                        marginTop: 4,
                        background:
                          mand > 0 ? 'var(--danger-bg, #fdecec)' : 'var(--warn-bg, #fdf6e3)',
                        padding: '8px 10px',
                        borderRadius: 8,
                      }}
                    >
                      {mand > 0 ? (
                        <b>
                          {faNum(mand)} گروه اصلی هنوز قیمت ندارد (الزامی) — تا آن‌ها بهای این
                          محصولات کمتر از واقعیت است.
                        </b>
                      ) : null}
                      {rec > 0 ? ` ${faNum(rec)} گروه پرتکرار هم بی‌قیمت است (توصیه‌شده).` : ''}{' '}
                      این‌ها را اول وارد کنید.
                    </p>
                  );
                })()}
                {loadStatus === 'error' ? (
                  <ErrorRetry error={loadError} onRetry={reloadPrices} />
                ) : loadStatus === 'loading' ? (
                  <p className="muted">در حال بارگذاری…</p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>گروه</th>
                        <th>سهم بها</th>
                        <th>قیمت امروز ﷼</th>
                        <th>عمر قیمت</th>
                        <th>ثبت‌کننده</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gus.length === 0 ? (
                        <tr>
                          <td className="empty" colSpan={6}>
                            گروهی ثبت نشده است.
                          </td>
                        </tr>
                      ) : null}
                      {gus.map((g) => {
                        const cur = prices[g.key];
                        const a = ageDays(cur?.date);
                        const ins = insight[g.key];
                        return (
                          <tr key={g.key}>
                            <td>
                              {g.group} <span className="muted">{g.unit}</span>{' '}
                              {g.main ? <Chip tone="success">اصلی</Chip> : null}{' '}
                              {ins && !ins.priced && ins.tier === 'mandatory' ? (
                                <Chip tone="danger">الزامی — بدون قیمت</Chip>
                              ) : ins && !ins.priced && ins.tier === 'recommended' ? (
                                <Chip tone="warn">توصیه‌شده — بدون قیمت</Chip>
                              ) : null}{' '}
                              {ins?.orphan ? <Chip tone="neutral">بدون مصرف در BOM</Chip> : null}
                            </td>
                            <td className="num">
                              {ins && ins.costShare > 0 ? formatPercent(ins.costShare) : '—'}
                            </td>
                            <td>
                              <NumberInput
                                style={{ maxWidth: 160 }}
                                value={draft[g.key] ?? (cur?.price ? String(cur.price) : '')}
                                onValue={(n) =>
                                  setDraft({ ...draft, [g.key]: n > 0 ? String(n) : '' })
                                }
                                disabled={!canEdit}
                              />
                            </td>
                            <td>{a != null ? `${a} روز` : '—'}</td>
                            <td className="muted">{cur?.updated_by ?? ''}</td>
                            <td>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  setComposition({ key: g.key, name: `${g.group} ${g.unit}` })
                                }
                              >
                                ترکیب
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  setGroupChart({ key: g.key, name: `${g.group} ${g.unit}` })
                                }
                              >
                                نمودار
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  setSimFor({
                                    key: g.key,
                                    name: `${g.group} ${g.unit}`,
                                    price: Number(draft[g.key] ?? cur?.price ?? 0),
                                  })
                                }
                              >
                                شبیه‌سازی
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {canEdit ? (
                  <div className="row" style={{ marginTop: 12 }}>
                    <Button onClick={save} loading={busy}>
                      ذخیرهٔ قیمت‌ها
                    </Button>
                  </div>
                ) : null}
              </Card>
            ),
          },
          { id: 'materials', label: 'مواد', render: () => <MaterialsCard canEdit={canEdit} /> },
          {
            id: 'market',
            label: 'بازار و شاخص',
            render: () => (
              <>
                <MarketCards canEdit={canEdit} />
                <TailReconciliationCard />
              </>
            ),
          },
        ]}
      />
      {groupChart ? (
        <HistoryModal
          kind="group"
          keyName={groupChart.key}
          title={groupChart.name}
          onClose={() => setGroupChart(null)}
        />
      ) : null}
      {composition ? (
        <GroupCompositionModal
          groupKey={composition.key}
          title={composition.name}
          insight={insight[composition.key]}
          onClose={() => setComposition(null)}
        />
      ) : null}
      {simFor ? (
        <GroupSimModal
          groupKey={simFor.key}
          title={simFor.name}
          initialPrice={simFor.price}
          onClose={() => setSimFor(null)}
        />
      ) : null}
    </>
  );
}

interface GroupSimResult {
  key: string;
  currentPrice: number | null;
  price: number;
  affectedProducts: number;
  avgDeltaPct: number | null;
  movers: {
    code: string;
    name: string;
    before: number;
    after: number;
    delta: number;
    deltaPct: number | null;
  }[];
}

/**
 * Group-price impact simulator (E16-05): a read-only what-if. Enter a hypothetical price for a group and see
 * how many products' «بهای تمام‌شده» moves and by how much — the biggest movers first — BEFORE committing the
 * price. Nothing is saved; the owner runs it, reads the blast radius, then closes and decides.
 */
function GroupSimModal({
  groupKey,
  title,
  initialPrice,
  onClose,
}: {
  groupKey: string;
  title: string;
  initialPrice: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const [price, setPrice] = useState(initialPrice > 0 ? String(initialPrice) : '');
  const [res, setRes] = useState<GroupSimResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    const p = Number(price);
    if (!(p > 0)) return toast('قیمت باید بزرگ‌تر از صفر باشد', 'error');
    setBusy(true);
    const r = await api<GroupSimResult>('/api/prices/group/simulate', {
      method: 'POST',
      body: JSON.stringify({ key: groupKey, price: p }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    setRes(r.body as GroupSimResult);
  }

  const up = res && res.avgDeltaPct != null && res.avgDeltaPct >= 0;
  return (
    <Modal title={`شبیه‌سازی قیمت گروه — ${title}`} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        یک قیمت فرضی وارد کنید تا ببینید بهای تمام‌شدهٔ چند محصول و چقدر جابه‌جا می‌شود. چیزی ذخیره
        نمی‌شود.
      </p>
      <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="قیمت فرضی (﷼ / واحد)">
          <NumberInput
            style={{ maxWidth: 180 }}
            value={price}
            onValue={(n) => setPrice(String(n))}
          />
        </Field>
        <Button onClick={run} loading={busy}>
          محاسبهٔ اثر
        </Button>
      </div>
      {res ? (
        <>
          <div className="totalbox" style={{ margin: '12px 0' }}>
            قیمت فعلی: <b className="num">{formatRial(res.currentPrice)}</b> ← فرضی:{' '}
            <b className="num">{formatRial(res.price)}</b> ·{' '}
            <b className="num">{faNum(res.affectedProducts)}</b> محصول متأثر ·{' '}
            {res.avgDeltaPct == null ? (
              '—'
            ) : (
              <>
                میانگین تغییر بها:{' '}
                <b className={up ? 'warn' : undefined}>
                  {up ? '+' : ''}
                  {formatPercent(res.avgDeltaPct)}
                </b>
              </>
            )}
          </div>
          {res.movers.length === 0 ? (
            <p className="muted">هیچ محصولی از این گروه متأثر نمی‌شود.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>محصول</th>
                  <th>بهای فعلی</th>
                  <th>بهای فرضی</th>
                  <th>تغییر</th>
                </tr>
              </thead>
              <tbody>
                {res.movers.map((m) => (
                  <tr key={m.code}>
                    <td>
                      {m.name} <span className="muted">{m.code}</span>
                    </td>
                    <td className="num">{formatRial(m.before)}</td>
                    <td className="num">{formatRial(m.after)}</td>
                    <td className="num">
                      <span className={m.delta >= 0 ? 'warn' : undefined}>
                        {m.delta >= 0 ? '+' : ''}
                        {formatRial(m.delta)}
                        {m.deltaPct == null
                          ? ''
                          : ` (${m.delta >= 0 ? '+' : ''}${formatPercent(m.deltaPct)})`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="muted" style={{ fontSize: 12 }}>
            بزرگ‌ترین جابه‌جایی‌ها نمایش داده می‌شود. برای اعمال، قیمت را در جدول گروه‌ها وارد و
            «ذخیرهٔ قیمت‌ها» را بزنید.
          </p>
        </>
      ) : null}
    </Modal>
  );
}

/**
 * What a group ACTUALLY prices: its materials, heaviest-used first, with the group's BOM footprint and cost
 * share — so the operator sees at a glance whether updating this group's price matters (groups-vs-BOM audit).
 */
function GroupCompositionModal({
  groupKey,
  title,
  insight,
  onClose,
}: {
  groupKey: string;
  title: string;
  insight: GroupInsightRow | undefined;
  onClose: () => void;
}) {
  const res = useApiResource<
    { code: string; name: string; unit: string; bomLines: number; specificPrice: boolean }[]
  >(`/api/prices/group-materials?key=${encodeURIComponent(groupKey)}`);
  return (
    <Modal title={`ترکیب گروه — ${title}`} onClose={onClose}>
      {insight ? (
        <p className="muted" style={{ marginTop: 0 }}>
          {insight.materials} ماده · {insight.bomLines} سطر BOM ·{' '}
          {insight.costShare > 0
            ? `سهم ${formatPercent(insight.costShare)} از بهای مواد`
            : 'بدون سهم بها'}
          {insight.orphan
            ? ' — هیچ محصولی از این گروه مصرف نمی‌کند؛ قیمت‌گذاری آن بر بهای هیچ موتوری اثر ندارد.'
            : ''}
        </p>
      ) : null}
      {res.status === 'error' ? (
        <ErrorRetry error={res.error} onRetry={res.reload} />
      ) : res.status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>ماده</th>
              <th>واحد</th>
              <th>سطرهای BOM</th>
              <th>قیمت</th>
            </tr>
          </thead>
          <tbody>
            {(res.data ?? []).length === 0 ? (
              <tr>
                <td className="empty" colSpan={4}>
                  ماده‌ای در این گروه نیست.
                </td>
              </tr>
            ) : null}
            {(res.data ?? []).map((m) => (
              <tr key={m.code}>
                <td>
                  {m.name} <span className="muted">{m.code}</span>
                </td>
                <td className="muted">{m.unit}</td>
                <td className="num">{m.bomLines}</td>
                <td>
                  {m.specificPrice ? (
                    <Chip tone="info">قیمت اختصاصی</Chip>
                  ) : (
                    <Chip>قیمت گروهی</Chip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Material-code pricing + bulk CSV import + coverage (NORTH_STAR §5) */
/* ------------------------------------------------------------------ */

function MaterialsCard({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MaterialRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [chartOf, setChartOf] = useState<{ code: string; name: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'actual' | 'group' | 'none'>('all');
  const [sortKey, setSortKey] = useState<'consumption' | 'affected' | 'price' | 'name' | 'code'>(
    'consumption',
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const loadCoverage = useCallback(async () => {
    const r = await api<Coverage>('/api/coverage');
    if (r.ok) setCoverage(r.body as Coverage);
  }, []);
  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  const search = useCallback(async (query: string, all: boolean) => {
    const limit = all ? 5000 : 50;
    const r = await api<MaterialRow[]>(
      `/api/materials?limit=${limit}&q=${encodeURIComponent(query)}`,
    );
    if (r.ok && Array.isArray(r.body)) setRows(r.body as MaterialRow[]);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => void search(q, showAll), 250);
    return () => clearTimeout(t);
  }, [q, showAll, search]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'code' ? 'asc' : 'desc');
    }
  };
  const arrow = (key: typeof sortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  // Client-side filter (by price source) + sort over whatever the server returned.
  const visible = useMemo(() => {
    const filtered = rows.filter((m) =>
      sourceFilter === 'all'
        ? true
        : sourceFilter === 'none'
          ? m.source == null
          : m.source === sourceFilter,
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name, 'fa') * dir;
      if (sortKey === 'code') return a.code.localeCompare(b.code) * dir;
      if (sortKey === 'price') return ((a.effectivePrice ?? -1) - (b.effectivePrice ?? -1)) * dir;
      if (sortKey === 'affected') return (a.affectedProducts - b.affectedProducts) * dir;
      return (a.consumption - b.consumption) * dir;
    });
  }, [rows, sourceFilter, sortKey, sortDir]);

  async function saveOne(code: string) {
    const price = Number(draft[code]);
    if (!(price > 0)) return toast('قیمت باید بزرگ‌تر از صفر باشد', 'error');
    const r = await postPrice('/api/prices/mat', { code, price }, confirm);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('قیمت ماده ذخیره شد');
    setDraft((d) => ({ ...d, [code]: '' }));
    void search(q, showAll);
    void loadCoverage();
  }

  return (
    <>
      <Card title="قیمت مواد (به تفکیک کد) — پوشش بهای واقعی">
        {coverage ? (
          <div className="totalbox" style={{ marginTop: 0, marginBottom: 12 }}>
            پوشش بهای <b>واقعی: {formatPercent(coverage.actualShare)}</b> · تخمینی (گروهی):{' '}
            {formatPercent(coverage.estimatedShare)} · مواد با قیمت انفرادی:{' '}
            <b className="num">{coverage.materialsWithIndividualPrice}</b>
            {coverage.unpricedGroupUnits > 0 ? (
              <span className="warn">
                {' '}
                · ⚠ {coverage.unpricedGroupUnits} گروه بی‌قیمت ({coverage.productsWithMissing} محصول
                ناقص)
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="جستجوی ماده (کد، نام، عنوان سپیدار)…"
            />
          </div>
          <select
            className="input"
            style={{ maxWidth: 170 }}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}
          >
            <option value="all">همهٔ منابع</option>
            <option value="actual">فقط واقعی</option>
            <option value="group">فقط گروهی</option>
            <option value="none">فقط بی‌قیمت</option>
          </select>
          <Button variant={showAll ? 'primary' : 'secondary'} onClick={() => setShowAll((v) => !v)}>
            {showAll ? '★ همهٔ مواد' : 'نمایش همه'}
          </Button>
          {canEdit ? (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              ⬆ ورود گروهی قیمت (CSV)
            </Button>
          ) : null}
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 6, fontSize: 12 }}>
          نمایش <b className="num">{visible.length}</b> ماده
          {showAll ? ' (کل لیست)' : ' — پرمصرف‌ترین‌ها؛ برای دیدن همه «نمایش همه» را بزنید'} · روی
          عنوان ستون‌ها برای مرتب‌سازی کلیک کنید.
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>
                <button type="button" className="th-sort" onClick={() => toggleSort('code')}>
                  کد ماده{arrow('code')}
                </button>
              </th>
              <th>
                <button type="button" className="th-sort" onClick={() => toggleSort('name')}>
                  نام{arrow('name')}
                </button>
              </th>
              <th>گروه</th>
              <th>واحد</th>
              <th>
                <button type="button" className="th-sort" onClick={() => toggleSort('consumption')}>
                  مصرف{arrow('consumption')}
                </button>
              </th>
              <th title="تعداد محصولاتی که این ماده در BOM آن‌هاست — دامنهٔ اثر تغییر قیمت">
                <button type="button" className="th-sort" onClick={() => toggleSort('affected')}>
                  محصولات متأثر{arrow('affected')}
                </button>
              </th>
              <th>
                <button type="button" className="th-sort" onClick={() => toggleSort('price')}>
                  قیمت مؤثر{arrow('price')}
                </button>
              </th>
              <th>قیمت انفرادی (﷼ / واحد)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td className="empty" colSpan={9}>
                  ماده‌ای با این فیلتر یافت نشد.
                </td>
              </tr>
            ) : null}
            {visible.map((m) => (
              <tr key={m.code}>
                <td className="num">{m.code}</td>
                <td>{m.name}</td>
                <td className="muted">{m.group}</td>
                <td className="muted">{m.unit || '—'}</td>
                <td className="num muted">
                  {m.consumption ? m.consumption.toLocaleString('en-US') : '—'}
                </td>
                <td
                  className="num"
                  title="محصولاتی که تغییر قیمت این ماده بهای آن‌ها را جابه‌جا می‌کند"
                >
                  {m.affectedProducts > 0 ? (
                    <span className={m.affectedProducts >= 10 ? 'warn' : undefined}>
                      {faNum(m.affectedProducts)}
                      {m.source == null && m.affectedProducts > 0 ? ' ⚠' : ''}
                    </span>
                  ) : (
                    <span className="muted">۰</span>
                  )}
                </td>
                <td>
                  {formatRial(m.effectivePrice)}{' '}
                  {m.source === 'actual' ? (
                    <Chip tone="success">واقعی</Chip>
                  ) : m.source === 'group' ? (
                    <Chip tone="info">گروهی</Chip>
                  ) : (
                    <Chip tone="danger">بی‌قیمت</Chip>
                  )}
                </td>
                <td>
                  <NumberInput
                    style={{ maxWidth: 150 }}
                    value={draft[m.code] ?? (m.individualPrice ? String(m.individualPrice) : '')}
                    onValue={(n) => setDraft({ ...draft, [m.code]: n > 0 ? String(n) : '' })}
                    disabled={!canEdit}
                  />
                </td>
                {canEdit ? (
                  <td>
                    <Button variant="ghost" onClick={() => saveOne(m.code)}>
                      ذخیره
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setChartOf({ code: m.code, name: m.name || m.code })}
                    >
                      نمودار
                    </Button>
                  </td>
                ) : (
                  <td>
                    <Button
                      variant="ghost"
                      onClick={() => setChartOf({ code: m.code, name: m.name || m.code })}
                    >
                      نمودار
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {chartOf ? (
        <HistoryModal
          kind="material"
          keyName={chartOf.code}
          title={chartOf.name}
          onClose={() => setChartOf(null)}
        />
      ) : null}
      {importOpen ? (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false);
            void search(q, showAll);
            void loadCoverage();
          }}
        />
      ) : null}
    </>
  );
}

function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<{
    summary: { total: number; create: number; update: number; skip: number };
    rows: ImportPreviewRow[];
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function doPreview() {
    setBusy(true);
    const r = await api<typeof preview>('/api/prices/mat/import/preview', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    setPreview(r.body as typeof preview);
  }

  async function doCommit() {
    setBusy(true);
    const r = await api<{ added: number; updated: number; skipped: number }>(
      '/api/prices/mat/import',
      { method: 'POST', body: JSON.stringify({ csv }) },
    );
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    const res = r.body as { added: number; updated: number; skipped: number };
    toast(`${res.added} افزوده، ${res.updated} به‌روزرسانی، ${res.skipped} رد شد`);
    onDone();
  }

  return (
    <Modal title="ورود گروهی قیمت مواد (CSV)" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        هر خط: <b className="num">کد ماده,قیمت</b> — با یا بدون سطر عنوان (کد ماده/قیمت). ارقام
        فارسی و جداکنندهٔ هزارگان پشتیبانی می‌شود.
      </p>
      <Field label="متن CSV (از اکسل کپی کنید)">
        <Textarea
          rows={7}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder={'2010100501,3500000\n2010100804,۹۰۰٬۰۰۰'}
        />
      </Field>
      <div className="row" style={{ marginTop: 10 }}>
        <Button variant="secondary" onClick={doPreview} loading={busy} disabled={!csv.trim()}>
          پیش‌نمایش
        </Button>
        {preview ? (
          <Button onClick={doCommit} loading={busy}>
            تأیید و اعمال ({preview.summary.create + preview.summary.update} قیمت)
          </Button>
        ) : null}
      </div>
      {preview ? (
        <>
          <div className="totalbox">
            کل: {preview.summary.total} · جدید: <b>{preview.summary.create}</b> · به‌روزرسانی:{' '}
            <b>{preview.summary.update}</b> ·{' '}
            <span className={preview.summary.skip ? 'warn' : ''}>رد: {preview.summary.skip}</span>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>خط</th>
                  <th>کد</th>
                  <th>قیمت</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line}>
                    <td className="num">{r.line}</td>
                    <td className="num">{r.code}</td>
                    <td className="num">
                      {typeof r.price === 'number' ? r.price.toLocaleString('en-US') : r.price}
                    </td>
                    <td>
                      {r.errors.length ? (
                        <span className="warn">{r.errors.join('، ')}</span>
                      ) : r.action === 'update' ? (
                        <Chip tone="info">به‌روزرسانی</Chip>
                      ) : (
                        <Chip tone="success">جدید</Chip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
